/**
 * Hooks de dados da tela Templates Meta WhatsApp (Brief 4 · Tela 3).
 *
 * Consome o CRUD real (apps/engine/src/modules/integrations/message-templates):
 *   - GET /v1/templates      (lista)   · gate `message-template-read`
 *   - GET /v1/templates/:id  (detalhe) · gate `message-template-read`
 *
 * As queries são GATEADAS por capability no `enabled` (não só no render) — sem
 * `message-template-read` o fetch nem dispara, evitando 403 vazando em toast
 * (lição "gatear o fetch, não só o render"). Mutações (gate `message-template-manage`)
 * chegam no PR2.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useCapability } from '../../hooks/use-capability';
import { api } from '../../lib/api-client';

import type { CreateTemplateBody, TemplateDto, UpdateTemplateBody } from './types';
import type { UseMutationResult } from '@tanstack/react-query';

const LIST_QUERY_KEY = 'message-templates-list';
const DETAIL_QUERY_KEY = 'message-template-detail';
const STALE_TIME = 60_000;

export function useTemplateList(): {
  data: TemplateDto[];
  isLoading: boolean;
  isError: boolean;
  canRead: boolean;
} {
  const canRead = useCapability('message-template-read');
  const query = useQuery({
    queryKey: [LIST_QUERY_KEY],
    queryFn: () => api.listTemplates(),
    enabled: canRead,
    staleTime: STALE_TIME,
    retry: 1,
  });

  return {
    data: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    canRead,
  };
}

export function useTemplate(id: string | null): {
  data: TemplateDto | undefined;
  isLoading: boolean;
  isError: boolean;
} {
  const canRead = useCapability('message-template-read');
  const query = useQuery({
    queryKey: [DETAIL_QUERY_KEY, id],
    queryFn: () => api.getTemplate(id!),
    enabled: canRead && id !== null,
    staleTime: STALE_TIME,
    retry: 1,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

// ── Mutações (PR2 · gate `message-template-manage`) ──────────────────────────

/**
 * Cria template (POST /v1/templates · nasce DRAFT). Em sucesso grava o DTO no
 * cache do detalhe e invalida a lista — o chamador navega para o editor. 409 de
 * nome duplicado propaga ao chamador. Espelha `useCreatePersona`.
 */
export function useCreateTemplate(): UseMutationResult<TemplateDto, unknown, CreateTemplateBody> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.createTemplate(body),
    onSuccess: (created) => {
      queryClient.setQueryData([DETAIL_QUERY_KEY, created.id], created);
      void queryClient.invalidateQueries({ queryKey: [LIST_QUERY_KEY] });
    },
  });
}

/**
 * Edita template (PATCH /v1/templates/:id · If-Match). Cobre tanto edição de
 * conteúdo (draft/rejected) quanto o toggle `enabled` (qualquer status). Em
 * sucesso grava o DTO (version++) no cache e invalida a lista. 409 (optimistic
 * lock) e 422 (`content-not-editable`/validação Meta) propagam ao chamador — a
 * view trata 409 com banner não-destrutivo e 422 refletindo o erro real.
 */
export function useUpdateTemplate(
  id: string,
): UseMutationResult<TemplateDto, unknown, { expectedVersion: number; body: UpdateTemplateBody }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ expectedVersion, body }) => api.updateTemplate(id, expectedVersion, body),
    onSuccess: (updated) => {
      queryClient.setQueryData([DETAIL_QUERY_KEY, id], updated);
      void queryClient.invalidateQueries({ queryKey: [LIST_QUERY_KEY] });
    },
  });
}

/**
 * Submete à Meta (POST /v1/templates/:id/submit-meta · If-Match). DRAFT/REJECTED
 * → SUBMITTED. Em dev sem credencial Meta o backend devolve 502 — a view trata
 * como estado honesto (toast "submissão indisponível"), NÃO como bug. 422 reflete
 * validação Meta; 409 = optimistic lock. Em sucesso atualiza cache + lista.
 */
export function useSubmitTemplateToMeta(
  id: string,
): UseMutationResult<TemplateDto, unknown, { expectedVersion: number }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ expectedVersion }) => api.submitTemplateToMeta(id, expectedVersion),
    onSuccess: (updated) => {
      queryClient.setQueryData([DETAIL_QUERY_KEY, id], updated);
      void queryClient.invalidateQueries({ queryKey: [LIST_QUERY_KEY] });
    },
  });
}
